import { useState } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { useRelativeTime } from '../../hooks/useRelativeTime';
import { NotificationRow } from './NotificationRow';

/**
 * A collapsed row representing N consecutive notifications about the same
 * entity by the same actor. Click anywhere on the summary line to expand
 * inline; the individual rows render via NotificationRow for parity.
 *
 * Props:
 *   group:        { id, items, isGroup }    — from groupConsecutive
 *   compact:      boolean                   — pass-through to NotificationRow
 *   onRowClick:   () => void                — pass-through, called when a
 *                                             child row is activated
 *   focused:      boolean                   — render focus ring on summary
 */
export function GroupedNotificationRow({ group, compact = false, onRowClick, focused = false }) {
  const [expanded, setExpanded] = useState(false);
  const items = group.items;
  const newest = items[0];
  const oldest = items[items.length - 1];
  const unreadCount = items.filter((n) => n.readAt == null).length;
  const timeLabel = useRelativeTime(newest.createdAt);

  const label = buildSummary(items);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="border-b border-gray-50 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={[
          'group flex w-full items-start gap-3 px-3 text-left transition-colors hover:bg-gray-50',
          compact ? 'py-2' : 'py-3',
          focused ? 'ring-2 ring-inset ring-blue-500' : '',
        ].join(' ')}
      >
        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
          <Layers className="h-4 w-4 text-blue-600" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-gray-900">{label}</span>
          <span className="mt-0.5 block text-xs text-gray-500">
            {items.length} updates · {timeLabel}
            {oldest !== newest && ' → '}
            {oldest !== newest && formatClockTime(oldest.createdAt)}
          </span>
        </span>
        <span className="flex flex-shrink-0 items-center gap-2 text-gray-400">
          {unreadCount > 0 && (
            <span
              className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-blue-100 px-1 text-xs font-semibold text-blue-700"
              aria-label={`${unreadCount} unread in group`}
            >
              {unreadCount}
            </span>
          )}
          <Chevron className="h-4 w-4" />
        </span>
      </button>
      {expanded && (
        <div className="bg-gray-50/40 pl-6">
          {items.map((n) => (
            <NotificationRow key={n.id} notification={n} compact={compact} onClick={onRowClick} />
          ))}
        </div>
      )}
    </div>
  );
}

function buildSummary(items) {
  const first = items[0];
  const entity = labelForEntity(first.entityType, first.entityId);
  return `${items.length} updates on ${entity}`;
}

function labelForEntity(entityType, entityId) {
  const id = entityId != null ? `#${entityId}` : '';
  switch (entityType) {
    case 'enquiry':       return `Enquiry ${id}`.trim();
    case 'quotation':     return `Quotation ${id}`.trim();
    case 'srf':           return `SRF ${id}`.trim();
    case 'customer':      return `Customer ${id}`.trim();
    case 'invoice':       return `Proforma invoice ${id}`.trim();
    case 'final_invoice': return `Final invoice ${id}`.trim();
    default:              return `this record ${id}`.trim();
  }
}

// Pure (non-hook) clock-time formatter used inside the grouped summary.
// Named without a `use` prefix to keep the rules-of-hooks lint happy.
function formatClockTime(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
